import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { OwlLoader } from "@/components/OwlLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF = `${API}/public/deck/providers.pdf`;

const NAVY = "#1A1330";
const INDIGO = "#3E2E65";
const LIME = "#A7C63F";
const ACCENT = "#5C7020";

const Bullets = ({ items, dark }) => (
  <ul className="space-y-3" data-testid="deck-bullets">
    {items.map((b, i) => (
      <li key={`${b}-${i}`} className="flex gap-3 items-start" style={{ animationDelay: `${i * 60}ms` }}>
        <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: dark ? LIME : INDIGO }} />
        <span className={`text-sm md:text-base leading-relaxed ${dark ? "text-white/85" : "text-slate-700"}`}>{b}</span>
      </li>
    ))}
  </ul>
);

const Boxes = ({ items, render, cols }) => (
  <div className={`grid gap-3 ${cols}`}>
    {items.map((it, i) => (
      <div key={`${it.title || it.h || it.label || ""}-${i}`} className="rounded-xl bg-slate-100/80 border border-slate-200 p-5">{render(it)}</div>
    ))}
  </div>
);

const Slide = ({ s }) => {
  const dark = s.kind === "cover" || s.kind === "contact";
  return (
    <div
      key={s.title}
      className="rounded-2xl border overflow-hidden relative"
      style={{ background: dark ? NAVY : "#fff", borderColor: dark ? "#3E2E65" : "#e2e8f0" }}
      data-testid="deck-slide"
    >
      <div className="absolute inset-x-0 bottom-0 h-1" style={{ background: LIME }} />
      <div className="px-7 md:px-14 py-10 md:py-14 min-h-[520px] flex flex-col">
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: dark ? LIME : ACCENT }}>
          {s.eyebrow}
        </div>
        <h2
          className={`mt-4 font-semibold tracking-tight ${s.kind === "cover" ? "text-4xl sm:text-5xl lg:text-6xl" : "text-2xl md:text-3xl"} ${dark ? "text-white" : "text-slate-900"}`}
          data-testid="deck-slide-title"
        >
          {s.title}
        </h2>
        {s.lead && (
          <p className={`mt-4 max-w-3xl text-sm md:text-base leading-relaxed ${dark ? "text-white/70" : "text-slate-500"}`}>
            {s.lead}
          </p>
        )}

        <div className="mt-8 space-y-8">
          {s.stats && (
            <Boxes
              items={s.stats}
              cols="grid-cols-2 md:grid-cols-4"
              render={(st) => (
                <>
                  <div className="text-3xl font-semibold" style={{ color: INDIGO }}>{st.value}</div>
                  <div className="mt-1 text-xs text-slate-500">{st.label}</div>
                </>
              )}
            />
          )}
          {s.columns && (
            <Boxes
              items={s.columns}
              cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-5"
              render={(col) => (
                <div className="flex flex-col h-full">
                  <div className="font-semibold text-slate-900">{col.head}</div>
                  <div className="mt-2 text-xs leading-relaxed text-slate-500 flex-1">{col.body}</div>
                  <div className="mt-4 text-[11px] font-semibold" style={{ color: ACCENT }}>{col.tag}</div>
                </div>
              )}
            />
          )}
          {s.steps && (
            <Boxes
              items={s.steps}
              cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              render={(st) => (
                <>
                  <div className="font-semibold" style={{ color: ACCENT }}>{st.head}</div>
                  <div className="mt-2 text-xs leading-relaxed text-slate-600">{st.body}</div>
                </>
              )}
            />
          )}
          {s.bullets && <Bullets items={s.bullets} dark={dark} />}
        </div>

        {s.footnote && (
          <div className={`mt-auto pt-10 text-xs ${dark ? "text-white/45" : "text-slate-400"}`}>{s.footnote}</div>
        )}
      </div>
    </div>
  );
};

export default function ProviderDeck() {
  const [deck, setDeck] = useState(null);
  const [i, setI] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/deck/providers`).then((r) => setDeck(r.data)).catch(() => setDeck({ slides: [] }));
  }, []);

  const total = deck?.slides?.length || 0;
  const go = useCallback((d) => setI((p) => Math.min(Math.max(p + d, 0), Math.max(total - 1, 0))), [total]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") go(1);
      if (e.key === "ArrowLeft" || e.key === "PageUp") go(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go]);

  if (!deck) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <OwlLoader size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-deck-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: ACCENT }}>
              Confidential · for prospective providers
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{deck.title}</h1>
          </div>
          <Button
            asChild
            className="rounded-full px-5"
            style={{ background: NAVY }}
            data-testid="deck-download-pdf-btn"
          >
            <a href={PDF} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </a>
          </Button>
        </div>

        <div className="mt-8">{total > 0 && <Slide s={deck.slides[i]} />}</div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => go(-1)} disabled={i === 0} data-testid="deck-prev-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => go(1)} disabled={i >= total - 1} data-testid="deck-next-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 flex flex-wrap gap-1.5 justify-center">
            {deck.slides.map((s, n) => (
              <button
                key={s.title || `slide-${n}`}
                onClick={() => setI(n)}
                aria-label={`Slide ${n + 1}`}
                data-testid={`deck-dot-${n}`}
                className="h-1.5 rounded-full transition-all"
                style={{ width: n === i ? 26 : 12, background: n === i ? INDIGO : "#cbd5e1" }}
              />
            ))}
          </div>
          <div className="text-xs text-slate-400 tabular-nums" data-testid="deck-counter">
            {i + 1} / {total}
          </div>
        </div>

        <p className="mt-8 text-xs text-slate-400">
          Use the arrow keys to move through the deck. Commercial placeholders are completed per provider before sending.
        </p>
      </div>
    </div>
  );
}
